import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Table as ReactTable,
  type Row as TableRow,
  type TableState,
} from "@tanstack/react-table";
import { createContext, useContext, useMemo } from "react";

import { cn } from "../../lib/cn";

import { Table } from "../table/table";
import { DataTableHeader } from "./data-table-header";
import {
  resolveDataTableCellStyle,
  resolveDataTableHeaderStyle,
  shouldGrowDataTableHeader,
  shouldGrowDataTableColumn,
} from "./data-table-layout";
import type { DataTableProps } from "./types";

type DataTableContextValue = {
  table: ReactTable<unknown>;
  tableState: TableState;
};

const DataTableContext = createContext<DataTableContextValue | null>(null);

function resolveSortableColumns<TData>(
  columns: ColumnDef<TData, unknown>[],
): ColumnDef<TData, unknown>[] {
  return columns.map((column) => ({
    ...column,
    enableSorting: column.meta?.sortable === true,
    ...("columns" in column && Array.isArray(column.columns)
      ? { columns: resolveSortableColumns(column.columns) }
      : {}),
  }));
}

export function useDataTableContext<TData>(): ReactTable<TData> {
  const context = useContext(DataTableContext);

  if (!context) {
    throw new Error("useDataTableContext must be used within a DataTable");
  }

  return context.table as ReactTable<TData>;
}

function SkeletonRows({
  cellDividers,
  colCount,
}: {
  cellDividers?: boolean;
  colCount: number;
}) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <Table.Row key={rowIndex}>
          {Array.from({ length: colCount }).map((__, colIndex) => (
            <Table.Cell
              key={colIndex}
              className={cn(
                cellDividers && "border-r last:border-r-0",
                "flex-1",
              )}
            >
              <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
            </Table.Cell>
          ))}
        </Table.Row>
      ))}
    </>
  );
}

export function DataTable<TData>({
  columns,
  data,
  loading = false,
  sorting,
  onChangeSorting,
  manualSorting = false,
  enableRowSelection,
  rowSelection,
  onChangeRowSelection,
  getRowId,
  getRowPinningPosition,
  onClickRow,
  toolbar,
  emptyState,
  bottomCaptureTriggerRef,
  bottomCaptureTriggerClassName,
  enablePagination = true,
  pagination,
  className,
  cellDividers = false,
  hideGroupedHeaders = false,
  headerClassName,
  rowClassName,
  size,
}: DataTableProps<TData>) {
  const sortableColumns = useMemo(
    () => resolveSortableColumns(columns),
    [columns],
  );
  const rowPinning = useMemo(() => {
    if (!getRowPinningPosition) {
      return undefined;
    }

    return data.reduce<{ bottom: string[]; top: string[] }>(
      (state, row, index) => {
        const position = getRowPinningPosition(row, index);
        const rowId = getRowId ? getRowId(row) : String(index);

        if (position === "top") {
          state.top.push(rowId);
        }

        if (position === "bottom") {
          state.bottom.push(rowId);
        }

        return state;
      },
      { bottom: [], top: [] },
    );
  }, [data, getRowId, getRowPinningPosition]);
  const table = useReactTable({
    data,
    columns: sortableColumns,
    state: {
      ...(sorting !== undefined ? { sorting } : {}),
      ...(rowSelection !== undefined ? { rowSelection } : {}),
      ...(rowPinning !== undefined ? { rowPinning } : {}),
    },
    enableRowSelection,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    autoResetPageIndex: enablePagination,
    ...(getRowPinningPosition !== undefined ? { keepPinnedRows: true } : {}),
    ...(onChangeSorting !== undefined
      ? { onSortingChange: onChangeSorting }
      : {}),
    ...(onChangeRowSelection !== undefined
      ? { onRowSelectionChange: onChangeRowSelection }
      : {}),
    ...(enablePagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
  });
  const tableState = table.getState();
  const contextValue = useMemo(
    () => ({ table: table as ReactTable<unknown>, tableState }),
    [table, tableState],
  );

  const headerGroups = table.getHeaderGroups();
  const leafColumnCount = table.getVisibleLeafColumns().length;
  const rows =
    rowPinning !== undefined
      ? [
          ...table.getTopRows(),
          ...table.getCenterRows(),
          ...table.getBottomRows(),
        ]
      : table.getRowModel().rows;
  const isEmpty = rows.length === 0 && !loading;

  return (
    <DataTableContext.Provider value={contextValue}>
      {toolbar}
      <Table.Root className={cn("isolate", className)}>
        {headerGroups.map((headerGroup) => {
          const hasGroupedHeaders = headerGroup.headers.some(
            (header) => header.subHeaders.length > 0,
          );

          if (hideGroupedHeaders && hasGroupedHeaders) {
            return null;
          }

          const resolvedHeaderClassName =
            typeof headerClassName === "function"
              ? headerClassName(headerGroup)
              : headerClassName;

          return (
            <Table.Header
              key={headerGroup.id}
              className={cn(
                resolvedHeaderClassName,
                hasGroupedHeaders && "bg-white",
              )}
            >
              {headerGroup.headers.map((header) => (
                <Table.Col
                  key={header.id}
                  style={resolveDataTableHeaderStyle(header)}
                  className={cn(
                    cellDividers && "border-r last:border-r-0",
                    shouldGrowDataTableHeader(header) && "flex-1",
                    header.column.columnDef.meta?.className,
                    header.column.columnDef.meta?.headerClassName,
                  )}
                  size={size}
                >
                  {header.isPlaceholder ? null : (
                    <DataTableHeader column={header.column}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </DataTableHeader>
                  )}
                </Table.Col>
              ))}
            </Table.Header>
          );
        })}

        {loading ? (
          <SkeletonRows
            cellDividers={cellDividers}
            colCount={leafColumnCount}
          />
        ) : null}
        {isEmpty ? emptyState : null}

        {!loading
          ? rows.map((row) => {
              const resolvedRowClassName =
                typeof rowClassName === "function"
                  ? rowClassName(row as TableRow<TData>)
                  : rowClassName;

              return (
                <Table.Row
                  key={row.id}
                  className={cn(
                    row.getIsSelected() && "bg-gray-100",
                    resolvedRowClassName,
                  )}
                  onClick={
                    onClickRow ? () => onClickRow(row.original) : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={resolveDataTableCellStyle(
                        cell.column.columnDef.meta?.stickyLeft,
                        cell.column.columnDef.size,
                      )}
                      className={cn(
                        cellDividers && "border-r last:border-r-0",
                        shouldGrowDataTableColumn(
                          cell.column.columnDef.meta?.className,
                          cell.column.columnDef.size,
                        ) && "flex-1",
                        cell.column.columnDef.meta?.className,
                        cell.column.columnDef.meta?.cellClassName,
                      )}
                      size={size}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })
          : null}
        {bottomCaptureTriggerRef ? (
          <div
            ref={bottomCaptureTriggerRef}
            className={cn("h-px shrink-0", bottomCaptureTriggerClassName)}
            aria-hidden="true"
          />
        ) : null}
      </Table.Root>

      {pagination}
    </DataTableContext.Provider>
  );
}
